import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getCountryCallingCode } from 'react-phone-number-input';

// Drop-in replacement for react-phone-number-input's native country <select>
// (pass as `countrySelectComponent`). The native select's popup height is
// drawn by the browser and can't be styled, so this renders its own compact,
// searchable list instead. Same props contract as the library's
// CountrySelectWithIcon: value, onChange(country | undefined), options,
// iconComponent, disabled, readOnly, onFocus, onBlur.
//
// The list is portalled to document.body (position: fixed) so page CSS that
// targets inputs inside the phone field (e.g. `.PhoneInputV3 input { border:
// none !important; padding: 0 !important }`) can't restyle its search box,
// and parent overflow can't clip it. It looks the same on every page.

const LIST_MAX_HEIGHT = 240;
const LIST_WIDTH = 280;
const GAP = 4;

const callingCodeOf = (country) => {
    if (!country) return '';
    try {
        return `+${getCountryCallingCode(country)}`;
    } catch (e) {
        return '';
    }
};

// Height of the search box row above the list.
const SEARCH_HEIGHT = 56;
// Keep the panel this far from the window edge.
const EDGE_MARGIN = 8;
// Never squeeze the list below this; below it the side with more room wins.
const LIST_MIN_HEIGHT = 120;

// Same flag images the library's country icon uses (default `flagUrl`).
const FLAG_URL = 'https://purecatamphetamine.github.io/country-flag-icons/3x2/{XX}.svg';
let flagsPreloaded = false;

// Flags are separate images fetched on first render of the list, so they used
// to pop in a moment after opening. Warm the browser cache once per page load.
const preloadFlags = (countries) => {
    if (flagsPreloaded || typeof Image === 'undefined') return;
    flagsPreloaded = true;
    setTimeout(() => {
        countries.forEach((country) => {
            const img = new Image();
            img.src = FLAG_URL.replace('{XX}', country);
        });
    }, 300);
};

const PhoneCountrySelect = ({
    value,
    onChange,
    options = [],
    iconComponent: Icon,
    disabled,
    readOnly,
    onFocus,
    onBlur,
    tabIndex,
    'aria-label': ariaLabel,
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    // Viewport position of the list — lined up with the whole phone field
    // (the .PhoneInput container), not just the flag inside its padding.
    const [panelPos, setPanelPos] = useState(null);
    const rootRef = useRef(null);
    const panelRef = useRef(null);
    const listRef = useRef(null);
    const searchRef = useRef(null);

    const isLocked = disabled || readOnly;

    const selectableOptions = useMemo(
        () => options.filter((opt) => !opt.divider),
        [options]
    );

    const filteredOptions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return selectableOptions;
        const qDigits = q.replace(/\D/g, '');
        return selectableOptions.filter((opt) => {
            const label = String(opt.label || '').toLowerCase();
            if (label.includes(q)) return true;
            return Boolean(qDigits) && callingCodeOf(opt.value).replace('+', '').startsWith(qDigits);
        });
    }, [selectableOptions, query]);

    const selectedOption = selectableOptions.find((opt) => opt.value === value);

    // Close on outside click.
    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (e) => {
            const inField = rootRef.current && rootRef.current.contains(e.target);
            const inPanel = panelRef.current && panelRef.current.contains(e.target);
            if (!inField && !inPanel) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        preloadFlags(selectableOptions.map((opt) => opt.value).filter(Boolean));
    }, [selectableOptions]);

    // Measures the phone field and places the list just below it, or above
    // when it doesn't fit below in the window. The list is portalled to body
    // (fixed), so only the window edges matter. If neither side has room for
    // the full list, it opens on the roomier side, shortened to fit.
    const updatePosition = useCallback(() => {
        const root = rootRef.current;
        if (!root) return;
        const fieldEl = root.closest('.PhoneInput') || root;
        const fieldRect = fieldEl.getBoundingClientRect();
        const spaceBelow = window.innerHeight - fieldRect.bottom - GAP - EDGE_MARGIN;
        const spaceAbove = fieldRect.top - GAP - EDGE_MARGIN;
        const needed = LIST_MAX_HEIGHT + SEARCH_HEIGHT;
        const above = spaceBelow < needed && spaceAbove > spaceBelow;
        const available = above ? spaceAbove : spaceBelow;
        const listHeight = Math.max(LIST_MIN_HEIGHT, Math.min(LIST_MAX_HEIGHT, available - SEARCH_HEIGHT));
        setPanelPos({
            left: fieldRect.left,
            width: fieldEl === root ? LIST_WIDTH : fieldRect.width,
            listHeight,
            ...(above
                ? { bottom: window.innerHeight - fieldRect.top + GAP }
                : { top: fieldRect.bottom + GAP }),
        });
    }, []);

    // Keep the list attached to the field while the page / modal / drawer
    // scrolls or the window resizes.
    useEffect(() => {
        if (!open) return undefined;
        const handleScroll = (e) => {
            // Scrolling the country list itself doesn't move the field.
            if (panelRef.current && panelRef.current.contains(e.target)) return;
            updatePosition();
        };
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, updatePosition]);

    // On open: reset search, position the list, focus the search box and
    // scroll the selected country into view.
    useLayoutEffect(() => {
        if (!open) {
            setPanelPos(null);
            return;
        }
        setQuery('');
        updatePosition();
    }, [open, updatePosition]);

    // Runs once the portalled list is in the DOM (after it got a position).
    useLayoutEffect(() => {
        if (!open || !panelPos || !panelRef.current || panelRef.current.dataset.ready) return;
        panelRef.current.dataset.ready = 'true';
        searchRef.current?.focus();
        const selectedEl = listRef.current?.querySelector('[data-selected="true"]');
        if (selectedEl && listRef.current) {
            listRef.current.scrollTop = selectedEl.offsetTop - listRef.current.clientHeight / 2 + selectedEl.clientHeight / 2;
        }
    }, [open, panelPos]);

    const handleSelect = (country) => {
        setOpen(false);
        onChange(country || undefined);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            setOpen(false);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (filteredOptions.length > 0) handleSelect(filteredOptions[0].value);
        }
    };

    return (
        <div ref={rootRef} className="PhoneInputCountry" style={{ position: 'relative' }}>
            <button
                type="button"
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                tabIndex={tabIndex}
                disabled={disabled}
                onClick={() => !isLocked && setOpen((prev) => !prev)}
                onFocus={onFocus}
                onBlur={onBlur}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    margin: 0,
                    cursor: isLocked ? 'default' : 'pointer',
                    height: '100%',
                }}
            >
                {Icon && <Icon country={value} label={selectedOption?.label} />}
                <div className="PhoneInputCountrySelectArrow" />
            </button>

            {open && panelPos && ReactDOM.createPortal(
                <div
                    ref={panelRef}
                    role="listbox"
                    onKeyDown={handleKeyDown}
                    // Keep clicks inside the list from reaching the field / modal.
                    onMouseDown={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        left: `${panelPos.left}px`,
                        ...(panelPos.bottom != null ? { bottom: `${panelPos.bottom}px` } : { top: `${panelPos.top}px` }),
                        width: `${panelPos.width}px`,
                        minWidth: '240px',
                        maxWidth: '90vw',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        // Above antd Modal / Drawer (1000).
                        zIndex: 1100,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    <div style={{ padding: '8px', borderBottom: '1px solid #F0F0F0' }}>
                        <input
                            ref={searchRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search country or code"
                            style={{
                                width: '100%',
                                height: '36px',
                                padding: '0 10px',
                                borderRadius: '8px',
                                border: '1px solid #E6E6E6',
                                outline: 'none',
                                boxSizing: 'border-box',
                                fontFamily: 'Roboto, sans-serif',
                                fontSize: '14px',
                                color: '#191919',
                                backgroundColor: '#FFFFFF',
                            }}
                        />
                    </div>
                    <div ref={listRef} style={{ maxHeight: `${panelPos.listHeight || LIST_MAX_HEIGHT}px`, overflowY: 'auto', padding: '4px 0', position: 'relative' }}>
                        {filteredOptions.length === 0 ? (
                            <div style={{ padding: '10px 14px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#8A8A8A' }}>
                                No matches found
                            </div>
                        ) : filteredOptions.map((opt) => {
                            const isSelected = opt.value === value;
                            return (
                                <div
                                    key={opt.value || 'ZZ'}
                                    role="option"
                                    aria-selected={isSelected}
                                    data-selected={isSelected ? 'true' : undefined}
                                    onClick={() => handleSelect(opt.value)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '8px 14px',
                                        cursor: 'pointer',
                                        fontFamily: 'Roboto, sans-serif',
                                        fontSize: '14px',
                                        color: isSelected ? '#594099' : '#191919',
                                        fontWeight: isSelected ? 600 : 400,
                                        backgroundColor: isSelected ? '#F3EFFF' : 'transparent',
                                    }}
                                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#F6F6F6'; }}
                                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                    {Icon && (
                                        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
                                            <Icon country={opt.value} label={opt.label} />
                                        </span>
                                    )}
                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {opt.label}
                                    </span>
                                    <span style={{ flexShrink: 0, color: '#8A8A8A', fontWeight: 400 }}>
                                        {callingCodeOf(opt.value)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default PhoneCountrySelect;
